import React, { useMemo, useState } from 'react';
import type { CatalogAdminResponse, CatalogService } from '../types';
import '../service-coverage.css';

const COVER_LAYERS: Array<[string, string]> = [
  ['route', 'Маршрут'],
  ['client', 'Приём клиента'],
  ['internal', 'Внутр. вопросы'],
  ['prelim', 'Предварительные'],
  ['materials', 'Материалы'],
  ['roles', 'Роли'],
  ['ai', 'Консультант'],
  ['addons', 'Дополнения'],
  ['locale', 'KZ / RU / EN'],
  ['qa', 'Контроль качества'],
];

type LayerState = 'READY' | 'REVIEW';

function statusClass(state: LayerState): string {
  return state === 'READY' ? 'ready' : 'review';
}

function statusLabel(state: LayerState): string {
  return state === 'READY' ? 'Готово' : 'Проверка';
}

interface Props {
  data: CatalogAdminResponse;
  onOpenQuestions: (serviceId: string) => void;
  onOpenAccess: () => void;
  onExport?: () => void;
}

/** Coverage matrix — template coverageView() + coveragePassport(). */
export const CoverageMatrixView: React.FC<Props> = ({
  data,
  onOpenQuestions,
  onOpenAccess,
  onExport,
}) => {
  const [q, setQ] = useState('');
  const [direction, setDirection] = useState('ALL');
  const [problemsOnly, setProblemsOnly] = useState(false);
  const [passport, setPassport] = useState<CatalogService | null>(null);

  const layerState = (service: CatalogService, key: string): LayerState => {
    const questions = data.questions.filter((x) => x.service_id === service.service_id && !x.is_deleted);
    const prelim = data.preliminaries.filter((x) => x.service_id === service.service_id && !x.is_deleted);
    const clients = data.client_mappings.filter((x) => x.service_id === service.service_id);
    const roles = data.role_access.filter((x) => x.service_id === service.service_id);
    if (key === 'route') return service.route ? 'READY' : 'REVIEW';
    if (key === 'client') return clients.length ? 'READY' : 'REVIEW';
    if (key === 'internal') return questions.length ? 'READY' : 'REVIEW';
    if (key === 'prelim') return prelim.length ? 'READY' : 'REVIEW';
    if (key === 'materials') return service.state === 'ACTIVE' ? 'READY' : 'REVIEW';
    if (key === 'roles') return roles.length ? 'READY' : 'REVIEW';
    if (key === 'ai') return data.ai_policy ? 'READY' : 'REVIEW';
    if (key === 'addons') return 'READY';
    if (key === 'locale') return 'READY';
    if (key === 'qa') return data.validation_issues.some((i) => i.object_id === service.service_id)
      ? 'REVIEW'
      : 'READY';
    return 'REVIEW';
  };

  const dirsById = useMemo(
    () => Object.fromEntries(data.directions.map((d) => [d.direction_id, d])),
    [data.directions],
  );

  const rows = useMemo(() => {
    return data.services
      .filter((s) => direction === 'ALL' || s.primary_direction_id === direction)
      .filter(
        (s) =>
          !q.trim() ||
          `${s.display_code} ${s.service_id} ${s.title}`
            .toLowerCase()
            .includes(q.trim().toLowerCase()),
      )
      .filter(
        (s) =>
          !problemsOnly || COVER_LAYERS.some(([k]) => layerState(s, k) !== 'READY'),
      );
  }, [data, direction, q, problemsOnly]);

  const questionCount = data.questions.filter((x) => !x.is_deleted).length;
  const prelimCount = data.preliminaries.filter((x) => !x.is_deleted).length;

  const passportDir = passport ? dirsById[passport.primary_direction_id] : null;
  const passportFacts = passport
    ? {
        questions: data.questions.filter(
          (x) => x.service_id === passport.service_id && !x.is_deleted,
        ).length,
        prelim: data.preliminaries.filter(
          (x) => x.service_id === passport.service_id && !x.is_deleted,
        ).length,
        clients: data.client_mappings
          .filter((x) => x.service_id === passport.service_id)
          .map((x) => x.category_title)
          .join(', '),
      }
    : null;

  return (
    <div className="service-coverage-matrix-root">
      <div className="service-coverage-toolbar" style={{ marginBottom: 12 }}>
        <button
          type="button"
          className={`service-coverage-btn${problemsOnly ? ' primary' : ''}`}
          onClick={() => setProblemsOnly((v) => !v)}
        >
          {problemsOnly ? 'Все строки' : 'Только проблемы'}
        </button>
        <button type="button" className="service-coverage-btn" onClick={onExport}>
          Экспорт снимка
        </button>
      </div>

      <div className="service-coverage-kpis">
        <article className="service-coverage-kpi" style={{ ['--kpi' as string]: '#41d2ff' }}>
          <small>Направления</small>
          <strong>{data.kpis.directions_count}</strong>
          <span>каталог направлений</span>
        </article>
        <article className="service-coverage-kpi" style={{ ['--kpi' as string]: '#49ddb7' }}>
          <small>Услуги</small>
          <strong>{data.kpis.services_count}</strong>
          <span>уникальные услуги</span>
        </article>
        <article className="service-coverage-kpi" style={{ ['--kpi' as string]: '#e2ad3a' }}>
          <small>Внутренние вопросы</small>
          <strong>{questionCount}</strong>
          <span>в реестре</span>
        </article>
        <article className="service-coverage-kpi" style={{ ['--kpi' as string]: '#9e80ff' }}>
          <small>Предварительные записи</small>
          <strong>{prelimCount}</strong>
          <span>по услугам</span>
        </article>
      </div>

      <section className="service-coverage-panel">
        <header>
          <div>
            <h3>Контрольная матрица · 10 слоёв</h3>
            <p>Клик по строке открывает паспорт покрытия услуги и связанные точки входа.</p>
          </div>
          <span className="service-coverage-spacer" />
          <input
            className="service-coverage-search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Код, услуга…"
          />
        </header>
        <div className="service-coverage-panel-body">
          <div className="service-coverage-toolbar">
            <button
              type="button"
              className={`service-coverage-chip service-coverage-chip-btn${
                direction === 'ALL' ? ' active' : ''
              }`}
              onClick={() => setDirection('ALL')}
            >
              Все
            </button>
            {data.directions.map((d) => (
              <button
                key={d.direction_id}
                type="button"
                className={`service-coverage-chip service-coverage-chip-btn${
                  direction === d.direction_id ? ' active' : ''
                }`}
                onClick={() => setDirection(d.direction_id)}
              >
                {d.display_code} · {d.title}
              </button>
            ))}
          </div>
          <div style={{ height: 10 }} />
          <div className="service-coverage-table-wrap">
            <table className="service-coverage-table">
              <thead>
                <tr>
                  <th>Услуга</th>
                  {COVER_LAYERS.map(([k, label]) => (
                    <th key={k}>{label}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((s) => {
                  const dir = dirsById[s.primary_direction_id];
                  const qCount = data.questions.filter(
                    (x) => x.service_id === s.service_id && !x.is_deleted,
                  ).length;
                  const pCount = data.preliminaries.filter(
                    (x) => x.service_id === s.service_id && !x.is_deleted,
                  ).length;
                  return (
                    <tr key={s.service_id} data-open="coverage" onClick={() => setPassport(s)}>
                      <td>
                        <b>{s.display_code}</b>
                        <br />
                        <strong>{s.title}</strong>
                        <br />
                        <small>{dir?.title || s.primary_direction_id}</small>
                      </td>
                      {COVER_LAYERS.map(([k]) => {
                        const st = layerState(s, k);
                        return (
                          <td key={k}>
                            <span className={`service-coverage-status ${statusClass(st)}`}>
                              {statusLabel(st)}
                            </span>
                            {k === 'internal' ? (
                              <>
                                <br />
                                <small>{qCount}</small>
                              </>
                            ) : null}
                            {k === 'prelim' ? (
                              <>
                                <br />
                                <small>{pCount}</small>
                              </>
                            ) : null}
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
                {!rows.length ? (
                  <tr>
                    <td colSpan={1 + COVER_LAYERS.length}>
                      <div className="service-coverage-empty">Услуг по фильтру нет</div>
                    </td>
                  </tr>
                ) : null}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      <div
        className={`service-coverage-drawer-backdrop${passport ? ' show' : ''}`}
        onClick={() => setPassport(null)}
      />
      <aside className={`service-coverage-drawer${passport ? ' open' : ''}`} aria-hidden={!passport}>
        {passport && passportFacts ? (
          <>
            <header>
              <div style={{ flex: 1 }}>
                <small className="service-coverage-route-kicker">Паспорт покрытия услуги</small>
                <h3 style={{ margin: '6px 0 0' }}>
                  {passport.display_code} · {passport.title}
                </h3>

              </div>
              <button
                type="button"
                className="service-coverage-btn"
                onClick={() => setPassport(null)}
              >
                Закрыть
              </button>
            </header>
            <div className="service-coverage-drawer-content">
              <div className="service-coverage-detail-grid">
                <div>
                  <small>Направление</small>
                  <strong>
                    {passportDir?.display_code || ''} ·{' '}
                    {passportDir?.title || passport.primary_direction_id}
                  </strong>
                </div>
                <div>
                  <small>Маршрут</small>
                  <strong>{passport.route || '—'}</strong>
                </div>
                <div>
                  <small>Архетип</small>
                  <strong>{passport.archetype || '—'}</strong>
                </div>
                <div>
                  <small>Категории клиентов</small>
                  <strong>{passportFacts.clients || '—'}</strong>
                </div>
                <div>
                  <small>Внутренние вопросы</small>
                  <strong>{passportFacts.questions}</strong>
                </div>
                <div>
                  <small>Предварительные записи</small>
                  <strong>{passportFacts.prelim}</strong>
                </div>
              </div>

              <h3 style={{ marginTop: 18, fontSize: 15 }}>Слои покрытия</h3>
              <div className="service-coverage-grid2" style={{ marginTop: 10 }}>
                {COVER_LAYERS.map(([k, label]) => {
                  const st = layerState(passport, k);
                  return (
                    <div key={k} className="service-coverage-card">
                      <strong>{label}</strong>
                      <div style={{ marginTop: 8 }}>
                        <span className={`service-coverage-status ${statusClass(st)}`}>
                          {statusLabel(st)}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>

              <div className="service-coverage-actions" style={{ marginTop: 16, justifyContent: 'flex-start' }}>
                <button
                  type="button"
                  className="service-coverage-btn"
                  onClick={() => {
                    onOpenQuestions(passport.service_id);
                    setPassport(null);
                  }}
                >
                  Открыть вопросы
                </button>
                <button
                  type="button"
                  className="service-coverage-btn"
                  onClick={() => {
                    onOpenAccess();
                    setPassport(null);
                  }}
                >
                  Доступ по ролям
                </button>
              </div>
            </div>
          </>
        ) : null}
      </aside>
    </div>
  );
};
